import cn from 'classnames';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import getFontDefinition from 'editor/src/store/fonts/selector/getFontDefinition';
import { FontDefinition } from 'editor/src/store/fonts/types';
import { useSelector } from 'editor/src/store/hooks';

import useOnClickOutside from 'editor/src/util/useOnClickOutside';

import FontFamiliesTabContent from 'editor/src/component/DesktopSidebar/TabContents/FontFamiliesTabContent';
import IconArrowDown from 'editor/src/component/Icon/IconArrowDown';
import { getFontDisplayName } from 'editor/src/component/TextStyleControl/textStylePreview';

import styles from './index.module.scss';

interface Props {
  fontFamily: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (fontFile: string) => void;
}

/**
 * GAPI-61895: the style's font in the style editor: its name in its own font, opening the
 * toolbar's font list (previews, search, uploaded fonts, "only uploaded fonts" and premium rules)
 * at the height the editor gives it. A click outside, Escape or a pick closes the list.
 */
function TextStyleFontPicker({ fontFamily, open, onOpenChange, onChange }: Props) {
  const { t } = useTranslation();
  const font = useSelector((state) => getFontDefinition(state, fontFamily));
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => onOpenChange(false), !open);

  const select = useCallback(
    ({ metadata }: FontDefinition) => {
      onOpenChange(false);
      if (metadata.fontFile !== fontFamily) {
        onChange(metadata.fontFile);
      }
    },
    [fontFamily, onChange, onOpenChange],
  );

  return (
    <div
      className={cn(styles.fontPicker, { [styles.fontPickerOpen]: open })}
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          onOpenChange(false);
        }
      }}
    >
      <button
        type="button"
        className={cn(styles.input, styles.fontTrigger, 'cy-text-style-font-trigger')}
        aria-label={t('editor-text-style-font')}
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <span className={styles.fontTriggerName} style={{ fontFamily: `"${fontFamily}", sans-serif` }}>
          {getFontDisplayName(fontFamily, font)}
        </span>
        <IconArrowDown />
      </button>
      {open && (
        <div className={cn(styles.fontPopover, 'cy-text-style-font-picker')}>
          <FontFamiliesTabContent onSelect={select} selectedFontFile={fontFamily} />
        </div>
      )}
    </div>
  );
}

export default React.memo(TextStyleFontPicker);
